<template>
  <!--
    P3 full-shell visual remediation (student-foundation-redesign, 00_*.md
    §13). Geometry measured directly from the rendered canonical prototype's
    `.app-header` (00_*.md §13.1, SHA-256
    6b1ff8b5031aee5b48fc3c71ca7ad303a098cfe62207e8584979d003c3df5dff), not
    approximated. Replaces the legacy full-width "در حال استفاده از برنا..."
    strip for the STUDENT context only (rendered by DashboardView/index.vue
    only when `!isVolunteer`; the volunteer `.role-bar` is untouched).

    Real functionality only: the role-switch button below drives the SAME
    `user/setActiveRole` action the old `.role-bar` used (passed in via
    props/events -- this component owns no store access), and the language
    switcher is the SAME real `LanguageSwitcher` component used elsewhere,
    just rendered in its compact header form. No notification bell -- it
    appears in the rendered prototype's header but is a forbidden product
    surface (00_*.md PF-11.2).

    CR-03 (doc 28 §0.2, §17): the AI Tutor credit pill is now shown -- a
    forward supersession of PF-11.1 for that pill ONLY (no teacher-credit
    pill, no wallet). It stays props-only like the rest of this header.
  -->
  <header class="student-app-header">
    <!--
      WP-04V-D (D5): the rendered prototype's mobile header carries the brand
      mark, not a page title. The title is desktop-only from here on, and the
      mark takes its place below the mobile breakpoint -- the sidebar (where
      the mark otherwise lives) is a drawer on mobile, so without this the
      header's whole leading edge is empty. Presentation only.
    -->
    <h2 class="student-app-header__title">{{ title }}</h2>
    <!-- P39 TR-034 (D-B1-3): the mobile mark goes home, like the sidebar's.
         The link now carries the show/hide rule; the image's «برنا» alt
         names it, so the image is no longer aria-hidden. -->
    <router-link
      :to="{ name: 'DashboardView' }"
      class="student-app-header__home"
      data-testid="student-header-brand-home"
    >
      <img class="student-app-header__brand" :src="bornaLogoUrl" alt="برنا" />
    </router-link>
    <div class="student-app-header__actions">
      <!--
        V405-F1 (doc 26 §12, F1-1, human decision 2026-09-29): the Student
        header no longer names the acting context in a sentence («در حال
        استفاده از برنا به‌عنوان دانش‌آموز»). The role switch below is the
        whole control; its `aria-label` still announces it in full. The
        volunteer role bar (DashboardView) keeps its own indicator.
      -->
      <button
        v-if="showRoleSwitch"
        type="button"
        class="student-app-header__role-switch"
        data-testid="role-switcher"
        :disabled="disabled"
        :aria-label="switchAriaLabel"
        @click="$emit('switch-role')"
      >
        <student-home-icon name="teacher" :size="17" />
        <span>
          <small>{{ $t('dashboard.roleSwitcher.switchCaption') }}</small>
          <strong>{{ switchToLabel }}</strong>
        </span>
        <!--
          STUDENT-UX-02 / UX2-02 (doc 30 §3, D-3): while the teacher
          capability is OFF the host marks the option Coming Soon with the
          existing amber badge (doc 22 §3, same style as HomeActionCard).
          Presentation only; the host decides what the click does.
        -->
        <span
          v-if="switchComingSoon"
          class="student-app-header__badge"
          data-testid="role-switcher-coming-soon"
          >{{ $t('studentHome.comingSoonBadge') }}</span
        >
        <student-home-icon name="arrow" :size="15" />
      </button>
      <!--
        CR-03 (doc 28 §17): the prototype's `credit-pill credit-pill--borna`,
        placed after the role switch as in the prototype. Approved deviation:
        NON-interactive (a span, not a button) -- there is no wallet page. The
        number is Subway's balance, formatted with the app's own locale digits;
        the accessible name says what it is. Hidden only when the balance is
        unknown (null).
      -->
      <span
        v-if="hasCredits"
        class="student-app-header__credits"
        role="img"
        data-testid="student-ai-tutor-credits"
        :title="creditsLabel"
        :aria-label="creditsLabel"
      >
        <student-home-icon name="coin" :size="18" />
        <strong>{{ creditsText }}</strong>
      </span>
      <!--
        WP-04V-C (D3): the language switcher moves OUT of the header and back
        into the sidebar, where the rendered prototype puts it (fifth nav row).
        See SidebarLinks.vue. The component itself is unchanged.
      -->
    </div>
  </header>
</template>

<script>
import StudentHomeIcon from '../StudentHome/StudentHomeIcon.vue'
import BornaLogoUrl from '@/assets/logo/borna-identity.png'
import { formatReportCount } from '@/utils/reportsFormat'

export default {
  name: 'StudentAppHeader',
  components: { StudentHomeIcon },
  data() {
    return { bornaLogoUrl: BornaLogoUrl }
  },
  props: {
    title: { type: String, required: true },
    showRoleSwitch: { type: Boolean, default: false },
    switchToLabel: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // UX2-02: the switch target is Coming Soon (badge + accessible name).
    switchComingSoon: { type: Boolean, default: false },
    // CR-03: Subway's Student AI Tutor credit balance; null = unknown.
    aiTutorCreditsRemaining: {
      type: Number,
      default: null,
    },
  },
  computed: {
    hasCredits() {
      const credits = this.aiTutorCreditsRemaining
      return typeof credits === 'number' && Number.isFinite(credits)
    },
    creditsText() {
      const locale = (this.$i18n && this.$i18n.locale) || 'fa'
      return formatReportCount(this.aiTutorCreditsRemaining, locale)
    },
    creditsLabel() {
      return this.$t('aiTutorSetup.credits.pillLabel', { n: this.creditsText })
    },
    switchAriaLabel() {
      const label = `${this.$t('dashboard.roleSwitcher.switchCaption')} ${
        this.switchToLabel
      }`
      return this.switchComingSoon
        ? `${label} (${this.$t('studentHome.comingSoonBadge')})`
        : label
    },
  },
}
</script>

<style lang="scss" scoped>
// Values measured directly from the rendered prototype's `.app-header` /
// `.dashboard-role-switch` (00_*.md §13.1).
.student-app-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  height: $borna-header-height;
  padding: 0 $borna-header-pad-x;
  background: rgba(245, 247, 252, 0.9);
  // WP-04V-C: the rendered prototype's header carries a hairline; this
  // shipped with none, so the header and the page ran together.
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  backdrop-filter: blur(6px);
  flex-wrap: wrap;
}

.student-app-header__title {
  font-size: $borna-fs-page-title;
  font-weight: $borna-fw-regular;
  line-height: 1.75;
  color: $borna-ink;
  margin: 0;
}

// Desktop shows the title; mobile shows the mark. Exactly one of the two is
// ever visible. P39 TR-034: the show/hide rule sits on the home link that now
// wraps the mark, so no empty link is left in the desktop row.
.student-app-header__home {
  display: none;
  line-height: 0;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid $borna-indigo-300;
    outline-offset: 4px;
  }
}

.student-app-header__brand {
  display: block;
  height: 49px;
  object-fit: contain;
  width: auto;
}

.student-app-header__actions {
  display: flex;
  align-items: center;
  gap: 9px;
}

.student-app-header__role-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 5px 10px;
  border-radius: 14px;
  border: 1px solid #dbe4ff;
  background: linear-gradient(135deg, #ffffff, #eef2ff);
  box-shadow: 0 5px 16px rgba(49, 75, 214, 0.08);
  color: #243ab4;
  font-size: 14px;
  line-height: 1.75;
  cursor: pointer;

  small {
    display: block;
    font-size: 10px;
    font-weight: 400;
    color: #65718d;
  }

  strong {
    display: block;
    font-weight: 700;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

// UX2-02: the existing amber «به‌زودی» pill (doc 22 §3; the values of
// HomeActionCard's `.home-action-card__badge`), sized for the 42px switch.
.student-app-header__badge {
  font-size: 11px;
  font-weight: 700;
  line-height: 1.6;
  color: #9a5b13;
  background: #fff7ed;
  border-radius: 999px;
  padding: 2px 9px;
  white-space: nowrap;
}

// The "arrow" glyph is copied directly from the rendered prototype's own
// RTL output (00_*.md §13.1) -- no mirroring transform needed.

// CR-03: the prototype's `.credit-pill.credit-pill--borna`, measured at 1440 /
// 390 / 360. No hover lift or pointer: it is not a control here.
.student-app-header__credits {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 55px;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #f0d790;
  border-radius: 999px;
  background: #fffaf0;
  color: #a56a00;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.75;

  svg {
    flex-shrink: 0;
    color: #d89a16;
  }

  strong {
    // The prototype's `var(--text)` (#0b163f), not the heading ink.
    color: $borna-text;
    font-weight: 900;
  }
}

// WP-04V-D. Two corrections here, both from the rendered measurement:
//
// 1. The breakpoint. This was 640px while `StudentAiTutorView` switched its
//    `calc(100vh - 72px|64px)` at 767px, so between those two widths the view
//    subtracted a header height the header did not have (root cause R7). Both
//    now use the single `borna-mobile` boundary, which is also the boundary
//    the JS `mobileMode` getter already uses.
// 2. The geometry. Measured 69px tall with 12px side padding; this shipped as
//    a 64px minimum with 16px.
@include borna-mobile {
  .student-app-header {
    padding: 0 $borna-header-pad-x-tablet;
    height: $borna-header-height-mobile;
    min-height: $borna-header-height-mobile;
  }

  .student-app-header__title {
    display: none;
  }

  .student-app-header__home {
    display: block;
  }

  .student-app-header__brand {
    height: $borna-header-brand-height-tablet;
  }
}

// Student Journey V2: the phone tier of the same header (V2 <=620px).
@include borna-phone {
  .student-app-header {
    padding: 0 $borna-header-pad-x-mobile;
  }

  .student-app-header__brand {
    height: 49px;
  }

  // CR-03: the prototype's phone tier of the credit pill (<=620px).
  .student-app-header__actions {
    gap: 4px;
  }

  .student-app-header__credits {
    gap: 4px;
    min-width: 43px;
    min-height: 36px;
    padding: 0 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
}

// CR-03: the prototype's narrowest tier of the credit pill (<=360px).
@media screen and (max-width: 360px) {
  .student-app-header__actions {
    gap: 3px;
  }

  .student-app-header__credits {
    min-width: 38px;
    padding: 0 4px;

    strong {
      font-size: 11px;
    }
  }
}
</style>
